import { ref, shallowRef } from 'vue';
import type { RoutePlan } from '@gpx-spa/geo-domain';
import { createLineBufferGeometryCache } from '@gpx-spa/geo-adapters';
import type { LineBufferGeometryCache } from '@gpx-spa/geo-adapters';
import { corridorBufferDefaultM } from '@/components/shipment/form/domain/account-defaults';
import { routeComposerPayloadSnapshot } from '@/components/shipment/form/utils/shipment-form-route-composer-snapshot';
import { computeStopsRouteFingerprint } from '@/utils/compute-stops-route-fingerprint';
import type { ShipmentFormStopRow } from '@/stores/shipment-form/stop-row';

export function createShipmentFormRouteComposerSlice() {
  const routePlan = ref<RoutePlan | null>(null);
  const corridorBufferM = ref(corridorBufferDefaultM());
  const geometryQualityPercent = ref(100);
  const geoCache = shallowRef(createLineBufferGeometryCache());
  const serverHydratedRouteFingerprint = ref<string | null>(null);
  const persistedRouteComposerSnapshot = ref<string | null>(null);

  function markRouteComposerPersisted(
    corridorBufferMeters: number,
    geometryQuality: number,
    selectedByPathId: Record<string, boolean>,
  ) {
    persistedRouteComposerSnapshot.value = routeComposerPayloadSnapshot({
      corridorBufferM: corridorBufferMeters,
      geometryQualityPercent: geometryQuality,
      selectedByPathId,
    });
  }

  function haveRouteComposerChangedSincePersisted(
    corridorBufferMeters: number,
    geometryQuality: number,
    selectedByPathId: Record<string, boolean>,
  ): boolean {
    if (persistedRouteComposerSnapshot.value == null) return true;
    return (
      routeComposerPayloadSnapshot({
        corridorBufferM: corridorBufferMeters,
        geometryQualityPercent: geometryQuality,
        selectedByPathId,
      }) !== persistedRouteComposerSnapshot.value
    );
  }

  function resetRouteComposerState() {
    routePlan.value = null;
    geoCache.value.clear();
    corridorBufferM.value = corridorBufferDefaultM();
    geometryQualityPercent.value = 100;
    serverHydratedRouteFingerprint.value = null;
  }

  function clearServerHydratedRouteFingerprint() {
    serverHydratedRouteFingerprint.value = null;
  }

  function markRoutePlanSyncedWithCurrentStops(rows: ShipmentFormStopRow[]) {
    const fingerprint = computeStopsRouteFingerprint(rows);
    if (fingerprint != null) {
      serverHydratedRouteFingerprint.value = fingerprint;
    }
  }

  return {
    routePlan,
    corridorBufferM,
    geometryQualityPercent,
    geoCache,
    serverHydratedRouteFingerprint,
    persistedRouteComposerSnapshot,
    markRouteComposerPersisted,
    haveRouteComposerChangedSincePersisted,
    resetRouteComposerState,
    clearServerHydratedRouteFingerprint,
    markRoutePlanSyncedWithCurrentStops,
  };
}

export type ShipmentFormRouteComposerSlice = ReturnType<
  typeof createShipmentFormRouteComposerSlice
>;
